import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useNavigate} from "react-router-dom";
import {CreditCardIcon, LogoutIcon, UserIcon} from './icons';
import authService from "../service/AuthService";
import {useSubscription} from '../hooks/useSubscription';
import {invalidateSubscriptionCache} from '../service/subscriptionCache';
import PlanBadge, {aiQuotaLine} from './PlanBadge';
import {formatDate} from '../utils/dateTime';

const menuItem = 'w-full text-left px-4 py-2.5 min-h-[44px] text-gray-700 dark:text-gray-300 ' +
    'hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-ui ease-out flex items-center gap-2';

const UserAvatar: React.FC = () => {
    const navigate = useNavigate();
    const [userInfo, setUserInfo] = useState<any>({});
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);
    // Тариф грузим при первом открытии меню, а не на каждом монтировании шапки:
    // до открытия он никому не виден (requirements FR-4).
    const {subscription, loading: planLoading, error: planError} = useSubscription(isDropdownOpen);

    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);

        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    useEffect(() => {
        async function fetchData() {
            setUserInfo(authService.getUserInfo());
        }

        fetchData();
    }, [authService.authenticated]);

    /** Закрытие меню всегда возвращает фокус на триггер — иначе он уходит в начало страницы. */
    const closeDropdown = useCallback(() => {
        setIsDropdownOpen(false);
        triggerRef.current?.focus();
    }, []);

    useEffect(() => {
        if (!isDropdownOpen) {
            return undefined;
        }
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                closeDropdown();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isDropdownOpen, closeDropdown]);

    const handleClickMobile = () => {
        if (authService.isAuthenticated()) {
            navigate('/profile');
        } else {
            navigate('/login');
        }
    };

    const onMenuClick = async (key: string) => {
        if (key === 'profile') {
            navigate('/profile');
            setIsDropdownOpen(false);
            return;
        }
        if (key === 'subscription') {
            navigate('/subscription');
            setIsDropdownOpen(false);
            return;
        }
        if (key === 'logout') {
            // Кеш подписки — состояние аккаунта: следующий вход в той же вкладке не
            // должен увидеть тариф предыдущего пользователя (requirements SEC-5).
            invalidateSubscriptionCache();
            await authService.logout();
            navigate('/login');
        }
        setIsDropdownOpen(false);
    };

    const onLoginClick = () => navigate('/login');

    const avatar = authService.isAuthenticated() ? (
        <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center">
            <UserIcon className="w-5 h-5 text-white"/>
        </div>
    ) : (
        <button
            className="btn-primary flex items-center space-x-2"
            onClick={onLoginClick}
        >
            <UserIcon className="w-4 h-4"/>
            <span>Войти</span>
        </button>
    );

    const displayName = userInfo ? userInfo.name : '';

    const content = (
        <div
            onClick={isMobile ? handleClickMobile : undefined}
            className="flex items-center justify-start p-2 cursor-pointer"
        >
            {avatar}
            {window.innerWidth >= 640 && (
                <div className="ml-3">
          <span className="text-gray-900 dark:text-gray-100 font-medium">
            {displayName}
          </span>
                </div>
            )}
        </div>
    );

    if (!authService.isAuthenticated()) {
        return content;
    }

    if (isMobile) {
        return content;
    }

    const quotaLine = aiQuotaLine(subscription?.quotas);
    const expiresAt = subscription?.expiresAt ? formatDate(subscription.expiresAt) : null;
    // Блок тарифа показываем, только когда есть что показать: при ошибке загрузки меню
    // открывается без него и навигация не страдает (requirements FR-14).
    const showPlanBlock = planLoading || (!planError && !!subscription);

    return (
        <div className="relative">
            <button
                ref={triggerRef}
                type="button"
                data-testid="user-avatar-trigger"
                aria-haspopup="menu"
                aria-expanded={isDropdownOpen}
                aria-label="Меню профиля"
                className="min-h-[44px] min-w-[44px] rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
                {content}
            </button>

            {isDropdownOpen && (
                <>
                    <div
                        className="fixed inset-0 z-10"
                        data-testid="user-avatar-backdrop"
                        onClick={() => setIsDropdownOpen(false)}
                    />
                    <div
                        role="menu"
                        aria-label="Меню профиля"
                        data-testid="user-avatar-menu"
                        className="fw-popover origin-top-right absolute right-0 mt-2 w-72 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20">
                        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                            <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                                {displayName || userInfo?.preferred_username || 'Профиль'}
                            </p>
                            {userInfo?.email && (
                                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{userInfo.email}</p>
                            )}
                            {showPlanBlock && (
                                <div className="mt-2 space-y-1" data-testid="user-avatar-plan">
                                    <PlanBadge
                                        planCode={subscription?.planCode}
                                        planName={subscription?.planName}
                                        status={subscription?.status}
                                        loading={planLoading}
                                    />
                                    {expiresAt && (
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            Действует до {expiresAt}
                                        </p>
                                    )}
                                    {quotaLine && (
                                        <p className="text-xs text-gray-500 dark:text-gray-400"
                                           data-testid="user-avatar-quota">
                                            {quotaLine}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="py-1">
                            <button
                                role="menuitem"
                                onClick={() => onMenuClick('profile')}
                                className={menuItem}
                            >
                                <UserIcon className="w-4 h-4"/>
                                <span>Профиль</span>
                            </button>
                            {subscription?.self && (
                                <button
                                    role="menuitem"
                                    data-testid="user-avatar-manage-subscription"
                                    onClick={() => onMenuClick('subscription')}
                                    className={menuItem}
                                >
                                    <CreditCardIcon className="w-4 h-4"/>
                                    <span>Управлять подпиской</span>
                                </button>
                            )}
                            <hr className="my-1 border-gray-200 dark:border-gray-700"/>
                            <button
                                role="menuitem"
                                onClick={() => onMenuClick('logout')}
                                className={menuItem}
                            >
                                <LogoutIcon className="w-4 h-4"/>
                                <span>Выход</span>
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default UserAvatar;
